import React from 'react';
import {reduxForm} from 'redux-form';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import {filesConsts as FILES_CONSTS} from '../../../constants/constants';
import {ImportAcceptFileTypes, ImportTypes} from '../../../enums/importTypes';
import PL_FilePicker from 'infrastructure/js/components/controls/FilePicker/filePicker';
import MessageDialog from 'infrastructure/js/components/Dialog/MessageDialog/messageDialog';
import PermissionManager from 'infrastructure/js/utils/permissionManager';

require('./importFilesJsonDialog.scss');

class ImportFilesJsonDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.dialogLabels = createLabelHelper('mat.dialog.');
    this.labels = createLabelHelper('mat.administration.matsettings.dialog.importFilesJson.');

    this.importType = this.props.sData?.get('importType');
    this.entityType = this.getEntityTypeByImportType(this.importType);

    this.state = {
      selectedFile: null,
      errors: null,
    }
  }

  getEntityTypeByImportType(importType) {
    switch(importType){
      case ImportTypes.IMPORT_QPL_JSON:
        return this.labels.get('entityType.qpl');
      case ImportTypes.IMPORT_QPL_FOR_MATERIAL_TYPES_JSON:
        return this.labels.get('entityType.qplForMaterialType');
      case ImportTypes.IMPORT_KIT_TYPES_JSON:
        return this.labels.get('entityType.kitTypes');
      case ImportTypes.IMPORT_TOOL_TYPES_JSON:
        return this.labels.get('entityType.toolTypes');
      default:
        console.log('getEntityTypeByImportType(): unknown import type: ', importType);
        return '';
    }
  }

  getValidationLabelsByImportType(importType) {
    switch(importType){
      case ImportTypes.IMPORT_QPL_JSON:
        return createLabelHelper('mat.wizards.import.files.qpl.validation.grid.operationstatus.');
      case ImportTypes.IMPORT_QPL_FOR_MATERIAL_TYPES_JSON:
        return createLabelHelper('mat.wizards.import.files.material.qpl.validation.grid.operationstatus.');
      case ImportTypes.IMPORT_KIT_TYPES_JSON:
        return createLabelHelper('mat.wizards.import.files.kittypes.validation.grid.operationstatus.');
      case ImportTypes.IMPORT_TOOL_TYPES_JSON:
        return createLabelHelper('mat.wizards.import.files.tooltypes.validation.grid.operationstatus.');
      default:
        console.log('getValidationLabelsByImportType(): unknown import type: ', importType);
        return '';
    }
  }

  getConfirmationEntityTypeByImportType(importType, response) {
    switch(importType){
      case ImportTypes.IMPORT_QPL_JSON:
      case ImportTypes.IMPORT_QPL_FOR_MATERIAL_TYPES_JSON:
        return this.entityType;
      case ImportTypes.IMPORT_KIT_TYPES_JSON:
        return createLabelHelper('mat.wizards.import.types.')?.get('kittypes', '', {count: response.totalItems});
      case ImportTypes.IMPORT_TOOL_TYPES_JSON:
        return createLabelHelper('mat.wizards.import.types.')?.get('tooltypes', '', {count: response.totalItems});
      default:
        console.log('getConfirmationEntityTypeByImportType(): unknown import type: ', importType);
        return '';
    }
  }

  hasAbbreviations(importType) {
    switch(importType) {
      case ImportTypes.IMPORT_QPL_JSON:
      case ImportTypes.IMPORT_QPL_FOR_MATERIAL_TYPES_JSON:
        return true;
      default:
        return false;
    }
  }

  getDialogButtons() {
    return {
      left: [
        {
          id: 'cancel',
          text: this.dialogLabels.get('cancel'),
          action: this.props.actions.hide,
        },
      ],
      right: [
        {
          id: 'submit',
          text: this.dialogLabels.get('import'),
          bsStyle: 'primary',
          disabled: this.state.selectedFile && !this.state.errors ? this.isLoading() : true,
          loading: this.isLoading(),
          action: this.props.handleSubmit(this.onSubmit),
        },
      ],
    };
  }

  onSubmit = () => {
    const importType = this.props.sData?.get('importType');
    this.props.actions.submit(this.state.selectedFile, this.getMessageDialogBuilder(), this.props.reloadParentComponent, {importType});
  };

  onHide = () => {
    this.props.actions.hide();
  }

  getMessageDialogBuilder = () => {
    return (response, fnCloseDialog) => {

     if (!response) return {};

      const confirmationEntityType = this.getConfirmationEntityTypeByImportType(this.importType, response);

      let title = this.labels.get('confirmation.title', undefined, {count: response.totalItems, entityType: confirmationEntityType});
      let className = 'import-files-json-confirmation-dialog';
      let children = [];
      let buttons = [
        {id:'close', text: this.dialogLabels.get('close'), action: fnCloseDialog , bsStyle: 'primary'}
      ];

      if (response.numberOfSucceededItems) {
        children.push(<MessageDialog.StatusRow key="row1" type={'succeeded'} count={response.numberOfSucceededItems}/>);
      }
      if (response.numberOfFailedItems) {
        children.push(<MessageDialog.StatusRow key="row2" type={'failed'} count={response.numberOfFailedItems}/>);
        children.push(<MessageDialog.MessageArea key="row3" text={this.getMessages(response, 'ERROR')}/>);
      }
      if (response.numberOfItemsWithWarnings) {
        children.push(<MessageDialog.StatusRow key="row4" type={'warning'} count={response.numberOfItemsWithWarnings}/>);
        children.push(<MessageDialog.MessageArea key="row5" text={this.getMessages(response, 'WARNING')}/>);
      }

      if (response.totalItems === 0) { //handle some specific cases when the server side was not able to get the items count
        if (!response.validationMessages?.length ) { //no items, no validations
          children.push(<MessageDialog.StatusRow key="row6" type={'succeeded'} count={response.totalItems}/>);
        }
        else if (response.validationMessages?.length > 0) { //no items, but some validations
          title = this.labels.get('title', '', {count: response.totalItems, entityType: confirmationEntityType});
          children.push(<MessageDialog.StatusRow key="row7" type={'failed'} count={''}/>);
          children.push(<MessageDialog.MessageArea key="row8" text={this.getMessages(response)}/>);
        }
      }

      return { title, children, type: 'error', className, buttons };
    };
  };

  getMessages = (data, severity=null) => {

    let validationMessages = data?.validationMessages?.filter((item) => {
      return severity ? (item.severity === severity) : (item.severity !== 'SUCCESS');
    });

    let messages = [];

    const validationLabels = this.getValidationLabelsByImportType(this.importType);

    validationMessages?.forEach((item, index) => {
      item.operationStatus?.forEach((item, statusIndex) => {
        let message = validationLabels.get(item.labelKey, null, item.params);
        messages.push(<div key={`${index}-${statusIndex}`}>{message}</div>);
      });
    });

    return messages;
  }

  renderErrors = () => {
    if(this.state.errors){
      return this.renderErrorSelection(this.state.errors);
    }
    return null;
  }

  renderErrorSelection = (data) => {
    let icon = 'pl pl-warning-icon';
    let errorType = data.statusType;
    return (
      <div className="error-selection">
        <div className='error-item'>
          <span className={`error-icon ${icon} ${errorType}`}/>
          <label className='error-msg'>{data.message}</label>
        </div>
      </div>
    );
  };

  handleSelectedFileChanged = (file) => {
    const maxFileSize = PermissionManager.getOrgPreferences().uploadMaxFileSize;

    if (file.size < maxFileSize * 1024) { // in bytes
      this.setState({selectedFile: file, errors: null});
      return;
    }

    let data = {
      errorCode: 'UF_10003',
      message : this.labels.get('errors.filesize', undefined, {maxSize: maxFileSize} ),
      statusType:'error'
    };
    this.setState({errors: data})
  };

  downloadDomSchema = () => {
    const importType = this.props.sData?.get('importType');
    this.props.actions.exportDomSchema(importType);
  }

  isLoading = () => {
    return this.props.sData?.get('loading');
  }

  render() {
    const entityType = this.hasAbbreviations(this.importType)  ? this.entityType : this.entityType?.toLowerCase();
    return (
      <Dialog
        id="import-files-json-dialog"
        className="import-files-json-dialog"
        show={true}
        onHide={this.onHide}
        titleText= {this.labels.get('title', '', {entityType: this.entityType})}
        footerButtons={this.getDialogButtons()}
        handleHashChange={false}
      >

        <div className={cn('upload-file') }>

          <label className="body-title">{this.labels.get('bodytitle', '', {entityType: entityType})}</label>
          <span>
            <label className="body-comment">{this.labels.get('bodycomment.start')}</label>
            <span className="settings-link" onClick={this.downloadDomSchema}>{this.labels.get('bodycomment.schema')}</span>
            <label className="body-comment">{this.labels.get('bodycomment.end', '', {entityType: entityType})}</label>
          </span>

          <PL_FilePicker
            name="selectedFileName"
            placeHolder={this.labels.get('selectfileplaceholder')}
            buttonText={this.state.selectedFile ? this.dialogLabels.get('changeFile') : this.dialogLabels.get('browse')}
            accept={[ImportAcceptFileTypes.JSON]}
            disabled={this.isLoading()}
            onSelectedFileChanged={this.handleSelectedFileChanged}
          />
        </div>

        {this.renderErrors()}

      </Dialog>
    );
  }
}

export default reduxForm({
  form: 'importFilesJsonDialog'
  }
)(ImportFilesJsonDialog);


ImportFilesJsonDialog.propTypes = {
  actions: PropTypes.object.isRequired,
  sData: PropTypes.object.isRequired,
};

